import type { Signal } from '@preact/signals-react'
import { batch, signal } from '@preact/signals-react'
import { uniqueElements } from '@susu/array'
import log from '@susu/headless-commerce/utils/log'
import { isNotNull, isNull } from '@susu/null'
import { isNotUndefined, isUndefined } from '@susu/undefined'
import type { Configurator } from '../Configurator/Configurator'
import type { Option } from '../Option/Option'
import type { Preload } from '../Preload/Preload'
import type { Step } from '../Step/Step'

export type Selected = Record<Step['name']['value'], string | null>

export class Selection {
  // The selection that was selected when the page loaded. Includes the preload
  // and recommendations.
  default: Signal<Selected>

  // The recommendations, based on the `recommendations` property of each step.
  recommended: Signal<Selected>

  // The selection that is currently selected.
  current: Signal<Selected>

  // The selection that was previously selected.
  // TODO: I'm not sure why this property is needed.
  previous: Signal<Selected>

  // Wether all options are out of stock for one step.
  areAllOptionsOutOfStock: Signal<boolean>

  constructor(
    defaultSelection: Selected,
    recommendedSelection: Selected,
    currentSelection: Selected,
    previousSelection: Selected,
  ) {
    this.default = signal(defaultSelection)
    this.recommended = signal(recommendedSelection)
    this.current = signal(currentSelection)
    this.previous = signal(previousSelection)
    this.areAllOptionsOutOfStock = signal(false)
  }

  setDefaultSelectionValue(key: Step['name']['value'], value: string | null) {
    this.default.value[key] = value
  }

  setCurrentSelectionValue(key: Step['name']['value'], value: string | null) {
    this.current.value[key] = value
  }

  setRecommendedSelectionValue(
    key: Step['name']['value'],
    value: string | null,
  ) {
    this.recommended.value[key] = value
  }

  /**
   * Ensures that the selection is in stock, by going through all options and
   * selecting the first one that is available.
   */
  ensureInStockSelection(configurator: Configurator, selection: Selected) {
    const newSelection: Selected = {}
    for (const [name, productId] of Object.entries(selection)) {
      const findFirstInStockOption = (step: Step) => {
        // Go through each option of the step and select the first one that is available.
        const firstInStockOption = step.getFirstInStockOption()
        if (
          isNotUndefined(firstInStockOption) &&
          isNotUndefined(firstInStockOption.product.value.id.value)
        ) {
          newSelection[name] = firstInStockOption.product.value.id.value
        } else {
          log.debug({
            method: 'Selection.ensureInStockSelection:findFirstInStockOption',
            name,
            productId,
            selection,
            firstInStockOption: {
              product: {
                name: firstInStockOption?.product.value.name.value,
                id: firstInStockOption?.product.value.id.value,
              },
            },
            options: step.options.value.map((x) => {
              return {
                product: {
                  name: firstInStockOption?.product.value.name.value,
                  id: firstInStockOption?.product.value.id.value,
                },
              }
            }),
          })
          throw new Error(
            `Selection.ensureInStockSelection:findFirstInStockOption: all options are out of stock for step: ${name}`,
          )
        }
      }

      if (isNull(productId)) {
        newSelection[name] = null
        continue
      }
      const step = configurator.findStepByName(name)
      if (!step) {
        continue
      }
      const option = step.findOptionByProductId(productId)
      if (!option) {
        // If the option isn't found then maybe it was recommended but not
        // available at this time, so pick another option.
        findFirstInStockOption(step)
      } else {
        const optionProductId = option.product.value.id

        // ProductId wasn't found on the option, use the next available option.
        if (isUndefined(optionProductId)) {
          findFirstInStockOption(step)
        } else {
          if (
            option.product.value.availability.value.isAvailable.value === false
          ) {
            // If it's not available, find the first available option.
            findFirstInStockOption(step)
          } else {
            newSelection[name] = productId
          }
        }
      }
    }
    return newSelection
  }

  /**
   * Returns the difference between the current selection and the default
   * selection.
   */
  getSelectionDifference(a: Selected, b: Selected) {
    const selection: Selected = {}
    for (const k in b) {
      if (a[k] !== b[k]) {
        selection[k] = b[k]
      }
    }
    return selection
  }

  /**
   * Given an option, returns the selection for that option.
   */
  getCurrentSelectedOptionSelection(option: Option) {
    const selection: Selected = {}
    if (isUndefined(option)) {
      throw new Error(
        'Configurator.getSelectedOptionSelection: No selected option',
      )
    }
    if (isUndefined(option.product.value.id.value)) {
      selection[option.step.value.name.value] = null
    } else {
      selection[option.step.value.name.value] = option.product.value.id.value
    }
    return selection
  }

  /**
   * Given a list of steps, returns the selection that is the productId of the
   * first option of each step. If an option does not have a product, it is not
   * included in the selection. If an option does not have an availability, it
   * is not included in the selection.
   */
  getDefaultOptionsSelection(steps: Array<Step>) {
    const selection: Selected = {}
    for (const step of steps) {
      // Here, we loop through all options instead of options (filtered), to
      // ensure that we look at all options that might otherwise be hidden due
      // to visibility filter.
      const availableOptions = step.allOptions.filter((option) => {
        // For options that have no product we act as if they are available, for
        // the other logic to pass.
        if (isUndefined(option.product.value.id.value)) {
          return true
        }
        return option.product.value.availability.value.isAvailable.value
      })
      if (availableOptions.length === 0) {
        selection[step.name.value] = null
      } else {
        const [firstOption] = availableOptions
        const productId = firstOption.product.value.id.value
        if (isNotUndefined(productId)) {
          selection[step.name.value] = productId
        } else {
          selection[step.name.value] = null
        }
      }
    }
    return selection
  }

  // Goes through all shown steps to make sure each shown step has a selection.
  getAllShownStepsSelection(configurator: Configurator) {
    const currentSelection = configurator.selection.value.current.value
    const selection: Selected = {}
    // Go through all shown steps
    for (const step of configurator.steps.value) {
      // If the current selection includes that step and it is null
      if (isNull(currentSelection[step.name.value])) {
        // Take the first option in the step.
        const firstOption = step.options.value[0]
        if (isNotUndefined(firstOption)) {
          if (isNotUndefined(firstOption.product.value.id.value)) {
            selection[step.name.value] = firstOption.product.value.id.value
          } else {
            selection[step.name.value] = null
          }
        }
      } else {
        // Otherwise just set the value to the current selection.
        selection[step.name.value] = currentSelection[step.name.value]
      }
    }
    return selection
  }

  /**
   * When passed a Configurator and Selected, returns a Selected object that has
   * all overrides of all steps applied.
   */
  getSelectionOverrideSelection(
    configurator: Configurator,
    selection: Selected,
  ) {
    const filteredSelection: Selected = {
      ...selection,
    }

    for (const step of configurator.steps.value) {
      for (const option of step.options.value) {
        const selectionOverrides = option.getSelectionOverrides()
        for (const selectionOverride of selectionOverrides) {
          delete filteredSelection[selectionOverride]
        }
      }
    }

    return filteredSelection
  }

  /**
   * When passed a Configurator and Selected, returns a Selected object that has
   * all preview selection overrides of the current step applied.
   */
  getPreviewSelectionOverrideSelection(
    configurator: Configurator,
    selection: Selected,
  ) {
    const filteredSelection: Selected = {
      ...selection,
    }

    const currentStep = configurator.currentStep.value
    if (isUndefined(currentStep)) {
      throw new Error('update: No current step')
    }
    for (const option of currentStep.options.value) {
      const selectionOverrides = option.getPreviewSelectionOverrides()
      for (const selectionOverride of selectionOverrides) {
        delete filteredSelection[selectionOverride]
      }
    }

    return filteredSelection
  }

  /**
   * Updates the default and current selection to the default options selection.
   */
  updateWithDefaultOptions(steps: Array<Step>) {
    const selection = this.getDefaultOptionsSelection(steps)

    for (const [key, value] of Object.entries(selection)) {
      this.setDefaultSelectionValue(key, value)
      this.setCurrentSelectionValue(key, value)
    }
  }

  /**
   * Updates the default and current selection to the preload selection.
   */
  updateWithPreload(preload: Preload) {
    const selection = preload.selection.value

    for (const [key, value] of Object.entries(selection)) {
      this.setDefaultSelectionValue(key, value)
      this.setCurrentSelectionValue(key, value)
    }
  }

  /**
   * Updates the default and current selection with the recommended selection.
   */
  updateWithRecommendations() {
    const selection = this.recommended.value

    for (const [key, value] of Object.entries(selection)) {
      this.setDefaultSelectionValue(key, value)
      this.setCurrentSelectionValue(key, value)
    }
  }

  /**
   * Updates the default, current, and recommended selection based on the
   * current situation of the configurator.
   */
  update(configurator: Configurator, selectedOption: Option) {
    batch(() => {
      // Get the current selected option so we can calculate the recommendations based on it.
      const selectedOptionSelection =
        this.getCurrentSelectedOptionSelection(selectedOption)
      for (const [key, value] of Object.entries(selectedOptionSelection)) {
        this.setCurrentSelectionValue(key, value)
      }

      const allShownStepsSelection =
        this.getAllShownStepsSelection(configurator)
      for (const [key, value] of Object.entries(allShownStepsSelection)) {
        this.setCurrentSelectionValue(key, value)
      }

      // Set the recommendations of the selectedOption in the recommended and current selection.
      for (const [key, value] of Object.entries(
        selectedOption.recommendations.value,
      )) {
        this.setRecommendedSelectionValue(key, value)
        this.setCurrentSelectionValue(key, value)
      }

      // Lastly, put the selectedOptionSelection on top to ensure that it is
      // selected.
      for (const [key, value] of Object.entries(selectedOptionSelection)) {
        this.setCurrentSelectionValue(key, value)
      }

      // Based on the step visibility, when the step is hidden, we need to
      // remove the selection of that step.
      this.default.value = this.removeHiddenSteps(
        configurator,
        this.default.value,
      )
      this.current.value = this.removeHiddenSteps(
        configurator,
        this.current.value,
      )

      // Then, apply the stock check.
      if (!configurator.isOneStepOutOfStock()) {
        this.current.value = this.ensureInStockSelection(
          configurator,
          this.current.value,
        )
      }
    })
  }

  /**
   * Removes the selection of hidden steps.
   */
  removeHiddenSteps(configurator: Configurator, value: Selected): Selected {
    const newValue: Selected = {}
    for (const [name, productId] of Object.entries(value)) {
      let step: Step | undefined
      // Don't use findStepByName because it will go through all steps and we
      // want only the shown steps here.
      for (const stepCandidate of configurator.steps.value) {
        if (stepCandidate.name.value === name) {
          step = stepCandidate
        }
      }

      // If it can't be found, it's hidden.
      if (isUndefined(step)) {
        newValue[name] = null
      } else {
        newValue[name] = productId
      }
    }
    return newValue
  }

  /**
   * Returns the recommended selection.
   */
  getRecommendedSelection(configurator: Configurator) {
    let selection: Selected = {}
    for (const step of configurator.steps.value) {
      const productId = this.current.value[step.name.value]
      if (!productId) {
        continue
      }
      const option = step.findOptionByProductId(productId)
      if (!option) {
        continue
      }
      selection = {
        ...selection,
        ...option.recommendations.value,
      }
    }
    return selection
  }

  /**
   * Updates the recommended selection.
   */
  updateRecommended(configurator: Configurator) {
    this.recommended.value = this.getRecommendedSelection(configurator)
  }

  /**
   * Returns true if the option is recommended.
   */
  isOptionRecommended(option: Option) {
    if (isUndefined(option.product.value.id.value)) {
      return false
    }
    return Object.values(this.recommended.value).includes(
      option.product.value.id.value,
    )
  }

  /**
   * Returns true if the option is selected.
   */
  isOptionSelected(option: Option) {
    const stepName = option.step.value.name.value
    const currentValue = this.current.value[stepName]

    if (isUndefined(option.product.value.id.value)) {
      return currentValue === null
    }

    return currentValue === option.product.value.id.value
  }

  /**
   * Serializes a Selected object into an array of product ids.
   * @param selection
   * @returns the product ids in the selection
   */
  toProductIds(selection: Selected): Array<string> {
    const productIds = uniqueElements(
      Object.values(selection).filter(isNotNull),
    )

    return productIds
  }

  /**
   * Encodes the selection to the QueryString format.
   */
  encodeToQueryString(selection: Selected): string {
    const result: string[] = []
    for (const [key, value] of Object.entries(selection)) {
      if (isNotNull(value)) {
        result.push(`${key}:${value}`)
      }
    }
    return result.join(',')
  }

  /**
   * Decodes the selection from the QueryString format.
   */
  decodeFromQueryString(queryString: string): Selected {
    if (queryString.length === 0) {
      return {}
    }

    const entries = queryString.split(',')
    const result: Selected = {}

    for (const entry of entries) {
      const [key, value] = entry.split(':')
      result[key] = value
    }

    return result
  }
}
